import React from 'react';
import { get } from 'react-hook-form';
import { Box, Flex, HStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import dataTypes from '../../../constants/dataTypes';
import colors from '../../../styles/theme/foundation/colors';
import UseFormMethodsType from '../../../types/UseFormMethodsType';
import { validateNotWhitespaceOnly } from '../../../utils/validationUtil';
import InputCaption from '../InputCaption';
import InputLabel from '../InputLabel';

import PinsAiSuggestion from './PinsAiSuggestion';
import { getInputType, getInputTypeStyle, inputTypes } from './util';

const FormInput = ({
  caption,
  inputName,
  isDisabled,
  isReadOnly,
  labelName,
  placeholder,
  required,
  type: inputType,
  dataType,
  useFormMethods,
  validationRules,
  toolTip,
  size,
  disableEnterKey,
  dataAnalyticsId,
  validateNoWhiteSpace,
  suggestedValues = null,
  duskSelector = undefined,
  cypressId = undefined,
  width = '100%',
  ...props
}) => {
  const type = dataType
    ? getInputType(dataType)
    : inputType;

  const inputTypeStyle = getInputTypeStyle(type);

  const {
    register,
    control,
    formState: { errors },
    resetField,
    setValue
  } = useFormMethods;

  validationRules = ((required || validationRules.required) && validateNoWhiteSpace)
    ? { ...validationRules, validate: validateNotWhitespaceOnly }
    : validationRules;

  const error = get(errors, inputName);

  const inputArgs = {
    size,
    placeholder,
    register,
    inputName,
    validationRules,
    isDisabled,
    isReadOnly,
    control,
    disableEnterKey,
    dataAnalyticsId,
    duskSelector,
    cypressId,
    error,
    resetField,
    setValue
  };

  if (type === 'checkbox' || type === 'switch') {
    return (
      <HStack alignItems="flex-start" height="50px" justifyContent="flex-start" spacing={4} width={width} {...props}>
        <Box minWidth="70%" width="auto">
          <InputLabel labelName={labelName} toolTip={toolTip} width="auto" />
        </Box>
        {inputTypeStyle.component(inputArgs)}
      </HStack>
    );
  }

  if (type === 'date') {
    const minDate = props?.minDate;
    const maxDate = props?.maxDate;

    return (
      <Box height="89px" w={width} {...props}>
        <InputLabel labelName={labelName} required={required} toolTip={toolTip} />
        {inputTypeStyle.component({ ...inputArgs, minDate, maxDate })}
      </Box>
    );
  }

  return (
    <Box
      w={width}
      {...props}
    >
      <HStack justifyContent="space-between">
        <InputLabel labelName={labelName} required={required} toolTip={toolTip} />
        {suggestedValues && (
          <PinsAiSuggestion
            inputName={inputName}
            suggestions={suggestedValues}
            useFormMethods={useFormMethods}
          />
        )}
      </HStack>
      <Flex
        _focusWithin={{
          boxShadow: isReadOnly ? 'none' : error ? `inset 0 0 0 1px ${colors.error.primary['600']}`
            : inputTypeStyle.styles?.inputBoxStyles?.boxShadow ?? `inset 0 0 0 1px ${colors.primaryScale['600']}`,
          bgColor: colors.greyScale['50']
        }}
        alignItems="center"
        bg={inputTypeStyle?.styles?.bg ?? colors.greyScale['100']}
        borderRadius={inputTypeStyle.styles?.inputBoxStyles?.borderRadius ?? '10px'}
        boxShadow={error && `inset 0 0 0 1px ${colors.error.primary['600']}`}
        flexDir="row"
        height={inputTypeStyle.styles.height}
        justifyContent={inputTypeStyle.styles.justifyContent}
        mt={inputTypeStyle.styles?.mt ?? 'inherit'}
        overflow={isReadOnly ? 'hidden' : 'visible'}
        style={inputTypeStyle.styles?.inputBoxStyles}
        textStyle="bodyMd"
        w="inherit"
      >
        {inputTypeStyle.component(inputArgs)}
      </Flex>
      <InputCaption caption={caption} error={error} />
    </Box>
  );
};

FormInput.propTypes = {
  caption: PropTypes.string,
  inputName: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  labelName: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(inputTypes)),
  dataType: PropTypes.oneOf(Object.values(dataTypes)),
  useFormMethods: UseFormMethodsType,
  validationRules: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  toolTip: PropTypes.string,
  size: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataAnalyticsId: PropTypes.string,
  duskSelector: PropTypes.string,
  cypressId: PropTypes.string,
  disableEnterKey: PropTypes.bool,
  validateNoWhiteSpace: PropTypes.bool,
  suggestedValues: PropTypes.arrayOf(PropTypes.shape({}))
};

FormInput.defaultProps = {
  type: inputTypes.standard,
  isDisabled: false,
  isReadOnly: false,
  labelName: '',
  caption: '',
  required: false,
  validationRules: {},
  toolTip: '',
  size: 'md',
  dataAnalyticsId: undefined,
  validateNoWhiteSpace: true
};

export default FormInput;
